
/* ============================== src/hooks/useTaskSubtasks.js ============================== */
// src/hooks/useTaskSubtasks.js
import { useMemo } from "react";
import { unwrapItems } from "../utils/dbShape";

/**
 * Подзадачи карточки: уже существующие (сохранены в db) и «черновые»
 * (пришли из применённого шаблона и появятся только после сохранения
 * родительской задачи). Оба списка нужны и вкладке «Подзадачи», и
 * saveHandler (для авто-простановки isSummary).
 *
 * db.tasks приходит либо массивом, либо конвертом { items, meta }
 * (серверная пагинация) — нормализуем через unwrapItems (utils/dbShape.js).
 *
 * serverSubtasks — ответ GET /tasks/:id/subtasks. Если передан массивом
 * (запрос завершён), он приоритетнее фильтра по кэшу: сервер учитывает
 * черновики-дети, созданные текущим пользователем (owner-scoped правило
 * в TaskRepo.applyDraftPolicy), которых нет в глобальном кэше db.tasks
 * (useTasksQuery идёт без includeDrafts). Пока serverSubtasks не пришёл
 * (undefined) или не применим (null) — используется локальный фильтр.
 */
export function useTaskSubtasks({
  db,
  taskId,
  projectId,
  isNew,
  pendingTemplateSubtasks,
  serverSubtasks = null,
}) {
  const tasks = useMemo(() => unwrapItems(db?.tasks), [db?.tasks]);

  const subtasks = useMemo(() => {
    if (Array.isArray(serverSubtasks)) return serverSubtasks;
    return tasks.filter((t) => t.parentTaskId === taskId);
  }, [tasks, taskId, serverSubtasks]);

  const draftSubtasks = useMemo(() => {
    if (!isNew || pendingTemplateSubtasks.length === 0) return [];
    return pendingTemplateSubtasks.map((node, idx) => ({
      id: `draft_${idx}`,
      _draft: true,
      title: node.title || "Без названия",
      assigneeId: null,
      status: "new",
      plannedHours: node.plannedHours ?? null,
      priority: node.priority || "mid",
      deadline: null,
      logs: [],
      projectId,
    }));
  }, [isNew, pendingTemplateSubtasks, projectId]);

  const displayedSubtasks = useMemo(
    () => [...draftSubtasks, ...subtasks],
    [draftSubtasks, subtasks],
  );

  return { subtasks, draftSubtasks, displayedSubtasks };
}
